import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, CheckCircle, XCircle, AlertTriangle, RefreshCw, Send, Image, Clock, TrendingUp, Users } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, subDays } from "date-fns";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

export function EmailNotificationsManager() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Fetch email notification stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["email_notification_stats"],
    queryFn: async () => {
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();

      // Total emails sent
      const { count: totalSent } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .gte("email_sent_at", thirtyDaysAgo);

      // Successful emails
      const { count: successCount } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("email_status", "sent")
        .gte("email_sent_at", thirtyDaysAgo);

      // Failed emails
      const { count: failedCount } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("email_status", "failed")
        .gte("email_sent_at", thirtyDaysAgo);

      // Bounced emails
      const { count: bouncedCount } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("email_status", "bounced")
        .gte("email_sent_at", thirtyDaysAgo);

      // Incomplete profile emails
      const { count: incompleteCount } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("notification_type", "incomplete_profile")
        .gte("email_sent_at", thirtyDaysAgo);

      // Picture reminder emails
      const { count: pictureCount } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("notification_type", "missing_picture")
        .gte("email_sent_at", thirtyDaysAgo);

      const successRate = totalSent ? ((successCount || 0) / totalSent * 100).toFixed(1) : "0";

      return {
        totalSent: totalSent || 0,
        successCount: successCount || 0,
        failedCount: failedCount || 0,
        bouncedCount: bouncedCount || 0,
        incompleteCount: incompleteCount || 0,
        pictureCount: pictureCount || 0,
        successRate,
      };
    },
  });

  // Fetch recent notifications
  const { data: notifications, isLoading: notificationsLoading, refetch } = useQuery({
    queryKey: ["email_notifications", typeFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("profile_email_notifications")
        .select(`
          id,
          user_id,
          notification_type,
          email_sent_at,
          email_status,
          attempt_number,
          created_at
        `)
        .order("email_sent_at", { ascending: false })
        .limit(100);

      if (typeFilter !== "all") {
        query = query.eq("notification_type", typeFilter);
      }
      if (statusFilter !== "all") {
        query = query.eq("email_status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch user emails
      const userIds = [...new Set(data?.map(n => n.user_id) || [])];
      let emails: Record<string, string> = {};
      
      if (userIds.length > 0) {
        const { data: privateData } = await supabase
          .from("profile_private")
          .select("user_id, email")
          .in("user_id", userIds);
        
        privateData?.forEach(p => {
          emails[p.user_id] = p.email;
        });
      }

      return data?.map(n => ({
        ...n,
        email: emails[n.user_id] || "Unknown",
      }));
    },
  });

  // Fetch effectiveness metrics
  const { data: effectiveness, isLoading: effectivenessLoading } = useQuery({
    queryKey: ["email_effectiveness"],
    queryFn: async () => {
      // Users who completed profile after receiving reminder
      // Using 'as any' to bypass type checking for new columns not yet in generated types
      const { data: completedAfterReminder } = await (supabase
        .from("profiles")
        .select("id, required_fields_completed_at, last_incomplete_reminder_sent_at") as any)
        .not("required_fields_completed_at", "is", null)
        .not("last_incomplete_reminder_sent_at", "is", null);

      const profileCompletions = (completedAfterReminder as any[])?.filter((p: any) => {
        if (!p.required_fields_completed_at || !p.last_incomplete_reminder_sent_at) return false;
        return new Date(p.required_fields_completed_at) > new Date(p.last_incomplete_reminder_sent_at);
      }).length || 0;

      // Calculate average time to completion
      let avgCompletionHours = 0;
      if (completedAfterReminder && completedAfterReminder.length > 0) {
        const validCompletions = (completedAfterReminder as any[]).filter((p: any) => {
          if (!p.required_fields_completed_at || !p.last_incomplete_reminder_sent_at) return false;
          return new Date(p.required_fields_completed_at) > new Date(p.last_incomplete_reminder_sent_at);
        });
        
        if (validCompletions.length > 0) {
          const totalHours = validCompletions.reduce((sum: number, p: any) => {
            const completed = new Date(p.required_fields_completed_at);
            const reminded = new Date(p.last_incomplete_reminder_sent_at);
            return sum + (completed.getTime() - reminded.getTime()) / (1000 * 60 * 60);
          }, 0);
          avgCompletionHours = totalHours / validCompletions.length;
        }
      }

      // Users who uploaded picture after receiving reminder
      const { data: pictureAfterReminder } = await (supabase
        .from("profiles")
        .select("id, profile_picture_uploaded_at, last_picture_reminder_sent_at") as any)
        .not("profile_picture_uploaded_at", "is", null)
        .not("last_picture_reminder_sent_at", "is", null);

      const pictureUploads = (pictureAfterReminder as any[])?.filter((p: any) => {
        if (!p.profile_picture_uploaded_at || !p.last_picture_reminder_sent_at) return false;
        return new Date(p.profile_picture_uploaded_at) > new Date(p.last_picture_reminder_sent_at);
      }).length || 0;

      // Total users who received reminders
      const { count: totalProfileReminders } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("notification_type", "incomplete_profile")
        .eq("email_status", "sent");

      const { count: totalPictureReminders } = await supabase
        .from("profile_email_notifications")
        .select("*", { count: "exact", head: true })
        .eq("notification_type", "missing_picture")
        .eq("email_status", "sent");

      return {
        profileCompletions,
        totalProfileReminders: totalProfileReminders || 0,
        profileConversionRate: totalProfileReminders ? ((profileCompletions / totalProfileReminders) * 100).toFixed(1) : "0",
        avgCompletionHours: avgCompletionHours.toFixed(1),
        pictureUploads,
        totalPictureReminders: totalPictureReminders || 0,
        pictureConversionRate: totalPictureReminders ? ((pictureUploads / totalPictureReminders) * 100).toFixed(1) : "0",
      };
    },
  });

  // Trigger incomplete profile reminders
  const triggerIncompleteReminders = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("send-incomplete-profile-reminders");
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Incomplete profile reminders sent! ${data.results?.firstReminders?.sent || 0} first, ${data.results?.secondReminders?.sent || 0} second reminders.`);
      queryClient.invalidateQueries({ queryKey: ["email_notifications"] });
      queryClient.invalidateQueries({ queryKey: ["email_notification_stats"] });
    },
    onError: (error: any) => {
      toast.error(`Failed to send reminders: ${error.message}`);
    },
  });

  // Trigger picture reminders
  const triggerPictureReminders = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("send-profile-picture-reminders");
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Picture reminders sent! ${data.results?.sent || 0} emails sent.`);
      queryClient.invalidateQueries({ queryKey: ["email_notifications"] });
      queryClient.invalidateQueries({ queryKey: ["email_notification_stats"] });
    },
    onError: (error: any) => {
      toast.error(`Failed to send reminders: ${error.message}`);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Sent</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case "bounced":
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="h-3 w-3 mr-1" />Bounced</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "incomplete_profile":
        return <Badge variant="outline" className="border-blue-300 text-blue-700"><Mail className="h-3 w-3 mr-1" />Profile</Badge>;
      case "missing_picture":
        return <Badge variant="outline" className="border-purple-300 text-purple-700"><Image className="h-3 w-3 mr-1" />Picture</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Emails (30 days)</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              {statsLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats?.totalSent}
              <Mail className="h-6 w-6 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              {stats?.incompleteCount} profile • {stats?.pictureCount} picture
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Success Rate</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2 text-green-600">
              {statsLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : `${stats?.successRate}%`}
              <CheckCircle className="h-6 w-6" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              {stats?.successCount} delivered successfully
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Failed / Bounced</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2 text-red-600">
              {statsLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : (stats?.failedCount || 0) + (stats?.bouncedCount || 0)}
              <XCircle className="h-6 w-6" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              {stats?.failedCount} failed • {stats?.bouncedCount} bounced
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Conversion Rate</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2 text-blue-600">
              {effectivenessLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : `${effectiveness?.profileConversionRate}%`}
              <TrendingUp className="h-6 w-6" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Profiles completed after reminder
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Effectiveness Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Effectiveness Metrics
          </CardTitle>
          <CardDescription>
            How well are our reminder emails performing?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 text-blue-700 mb-2">
                <Users className="h-5 w-5" />
                <span className="font-semibold">Profile Completions</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {effectivenessLoading ? "..." : effectiveness?.profileCompletions}
              </div>
              <div className="text-sm text-blue-600">
                of {effectiveness?.totalProfileReminders} reminded users ({effectiveness?.profileConversionRate}%)
              </div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <Clock className="h-5 w-5" />
                <span className="font-semibold">Avg Time to Complete</span>
              </div>
              <div className="text-2xl font-bold text-green-900">
                {effectivenessLoading ? "..." : `${effectiveness?.avgCompletionHours}h`}
              </div>
              <div className="text-sm text-green-600">
                After receiving reminder email
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 text-purple-700 mb-2">
                <Image className="h-5 w-5" />
                <span className="font-semibold">Picture Uploads</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {effectivenessLoading ? "..." : effectiveness?.pictureUploads}
              </div>
              <div className="text-sm text-purple-600">
                of {effectiveness?.totalPictureReminders} reminded users ({effectiveness?.pictureConversionRate}%)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual Trigger Buttons */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Manual Triggers
          </CardTitle>
          <CardDescription>
            Manually trigger reminder emails for testing or immediate sending
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => triggerIncompleteReminders.mutate()}
              disabled={triggerIncompleteReminders.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {triggerIncompleteReminders.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Send Incomplete Profile Reminders Now
            </Button>

            <Button
              onClick={() => triggerPictureReminders.mutate()}
              disabled={triggerPictureReminders.isPending}
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              {triggerPictureReminders.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Image className="h-4 w-4 mr-2" />
              )}
              Send Picture Reminders Now
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                refetch();
                queryClient.invalidateQueries({ queryKey: ["email_notification_stats"] });
                queryClient.invalidateQueries({ queryKey: ["email_effectiveness"] });
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Recent Email Notifications</CardTitle>
              <CardDescription>
                Last 100 notification emails sent
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="incomplete_profile">Profile Reminders</SelectItem>
                  <SelectItem value="missing_picture">Picture Reminders</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="bounced">Bounced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {notificationsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : notifications?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No email notifications found
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempt</TableHead>
                    <TableHead>Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications?.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {notification.email}
                      </TableCell>
                      <TableCell>
                        {getTypeBadge(notification.notification_type)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(notification.email_status)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">#{notification.attempt_number}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(notification.email_sent_at), "MMM d, yyyy h:mm a")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
